import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { deleteLiverampSegment } from '~/src/dash/api/console-api';
import {
  distributeSegments,
  getLiverampSegmentsFromDistributionManagerWithPagination,
} from '~/src/dash/api/liveramp-distribution/liveramp_distribution_methods';
import { ILiverampDistributionListInfo } from '~/src/dash/api/liveramp-distribution/liveramp_distribution_model';
import { getFilterParams, IFiltersState } from '~/src/dash/components/filters/common';
import { FilterRow } from '~/src/dash/components/filters/FilterRow';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ModelWithTitleConfirmation } from '~/src/dash/components/model-with-title-confirmation';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  spacingV2,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  RouteAudienceManagerLiverampMarketplace,
  RouteAudienceManagerSettingsList,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { formatBiddersResponseIntoLVTRows } from '~/src/dash/views/segments/audience/liveramp/distribution/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const BULK_ACTION_COLOR = publicaColors.blue;

export const ManageLiveRampDistribution = () => {
  const { session, user } = useBaseStore();
  const { LiverampDistribution } = useBaseConfig();
  const cfg = LiverampDistribution.Main;
  const { cypressClassName, defaultState } = cfg;
  const isSamsung = session.publisher.ID === '394';
  const isSuperUser = hasRoleSuperUser(user.user);
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || '');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  const [responseData, setResponseData] = useState<ILiverampDistributionListInfo[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const rows = formatBiddersResponseIntoLVTRows(responseData, cfg.table);
  const [paginationToken, setPaginationToken] = useState('');
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [deleteBulkModalActive, setIsDeleteModalActive] = useState<boolean>(false);
  const [activeColumnsList] = useState<IMenuList>(
    extractVisibleColumns(cfg.table.columns, defaultState.activeColumnKeys || [], isSuperUser),
  );
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<IFiltersState>({});
  const [bulkRemoveDisabled, setBulkRemoveDisabled] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState<boolean>(false);
  const [currentRowId, setCurrentRowId] = useState<string>('');
  const [currentItemName, setCurrentItemName] = useState<string>('');

  const handleCloseDeleteModal = () => {
    setDeleteModalActive(false);
  };

  const handleOpenDeleteModal = (id: string, currItName: string) => {
    setCurrentItemName(currItName);
    setCurrentRowId(id);
    setDeleteModalActive(true);
  };

  const MarketplaceSegmentButton = () => {
    return (
      <ButtonV2
        clearBackground
        onClick={() => {
          track('LiveRamp Distribution - Access marketplace table', { isSuperUser }, user.user);
          navigate(RouteAudienceManagerLiverampMarketplace, extractRouteParams({ session, user }));
          return;
        }}
        backgroundColor='transparent'
        style={{ padding: '5px 16px' }}
      >
        <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
          <Col marginTop='6px'>
            <IconV2 name='boxSearch' color={publicaColors.blue} />
          </Col>
          <Col>
            <Text small bold color={publicaColors.blue}>
              Data Marketplace Segments
            </Text>
          </Col>
        </Cols>
      </ButtonV2>
    );
  };

  const getData = () => {
    setIsTableActionPending(true);
    setLoadState(LoadingStateLoading);
    const filterParams = getFilterParams(filters, cfg.filters);
    // create query object
    const urlQueries = {
      pageSize: rowsPerPage,
      bidderGroupIDs: [],
      page,
      search: query,
      orderBy,
      orderDirection: order,
      paginationToken,
      ...filterParams,
    };

    // update config
    cfg.defaultState.orderBy = orderBy;
    cfg.defaultState.order = order;
    (cfg.defaultState.page = page), (cfg.defaultState.rowsPerPage = rowsPerPage);

    getLiverampSegmentsFromDistributionManagerWithPagination(session.publisher.ID, {
      urlQueries,
    }).then(([distributionData, err]) => {
      if (err !== undefined) {
        // handle error
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Error fetching segment.');
          setSnackbarError(true);
          setLoadState(LoadingStateHasData);
        });
        return;
      }
      const data: ILiverampDistributionListInfo[] =
        (distributionData && distributionData.requested_segments) || [];

      if (distributionData) {
        setResponseData(data);
        setTotalRowCount(distributionData.total_count || 0);
        setLoadState(LoadingStateHasData);
        if (distributionData.pagination_token !== '') {
          setPaginationToken(distributionData.pagination_token);
        }
      }
      setIsTableActionPending(false);
    });
  };

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, query, filters, orderBy, order]);

  const handleChangePage = (_e: unknown, newPage: number) => {
    unstable_batchedUpdates(() => {
      setSelected([]);
      setPage(newPage);
    });
  };

  const isDistributed = (rowID: string) => {
    for (const ele of responseData) {
      if (String(ele.SegmentID) === rowID && ele.AssociationLiverampSegmentID !== 0) {
        return true;
      }
    }
    return false;
  };

  const handleDeleteItem = async (id: string) => {
    track('LiveRamp Distribution - Remove from distribution', { isSuperUser }, user.user);
    setIsTableActionPending(true);
    setLoadState(LoadingStateLoading);
    try {
      await deleteLiverampSegment(session.publisher.ID, id);
      unstable_batchedUpdates(() => {
        setIsTableActionPending(false);
        setSnackbarMessage('Successfully deleted segment.');
        setSnackbarError(false);
      });
      getData();
    } catch (err) {
      console.error(err);
      unstable_batchedUpdates(() => {
        setIsTableActionPending(false);
        setSnackbarMessage('Error while deleting segment.');
        setSnackbarError(true);
        setLoadState(LoadingStateHasData);
      });
    }
    return new Promise((resolve) => resolve(undefined));
  };

  const handleOnChangeSelect = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    const allRequested = !newSelected.some((ele) => isDistributed(ele));

    setBulkRemoveDisabled(allRequested);
    setSelected(newSelected);
  };

  const handleOnClickBulkDelete = () => {
    setIsDeleteModalActive(true);
  };

  const handleCloseBulkDeleteModal = () => {
    unstable_batchedUpdates(() => {
      setIsDeleteModalActive(false);
      setSelected([]);
    });
  };

  const handleAddToDistribution = async () => {
    track('LiveRamp Distribution - Add to distribution', { isSuperUser }, user.user);
    setIsTableActionPending(true);
    const urlQueries = {
      segmentIDs: selected.join(','),
    };
    try {
      const result = await distributeSegments(session.publisher.ID, { urlQueries });
      if (result && result.success) {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            `Success! Your segment(s) will arrive at your destination within 1-3 days, plus any destination-specific processing time.`,
          );
          setSnackbarError(false);
          setSelected([]);
          getData();
        });
      } else {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Error distributing Segments. Please refresh.');
          setSnackbarError(true);
        });
      }
    } catch (err) {
      console.error(err);
      unstable_batchedUpdates(() => {
        setIsTableActionPending(false);
        setSnackbarMessage('Error distributing Segments. Please refresh.');
        setSnackbarError(true);
      });
    }
  };

  const handleDeleteBulkBidders = () => {
    track('LiveRamp Distribution - Remove from distribution', { isSuperUser }, user.user);
    setIsTableActionPending(true);
    setLoadState(LoadingStateLoading);
    const numSelected = selected.length;
    const deleteSegments: Array<Promise<unknown>> = [];
    selected.forEach((id) => {
      deleteSegments.push(deleteLiverampSegment(session.publisher.ID, id));
    });
    Promise.all(deleteSegments)
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(`Successfully deleted ${numSelected} Segments.`);
          setSnackbarError(false);
          setSelected([]);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Error deleting Segments. Please refresh.');
          setSnackbarError(true);
          setLoadState(LoadingStateHasData);
        });
      });
    return Promise.resolve(undefined);
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  const handleOnChangeQuery = (text: string) => {
    track('LiveRamp Distribution - Search', { isSuperUser }, user.user);
    setQuery(text);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setSelected([]);
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.map((n: any) => n.ID.value));
    } else {
      setSelected([]);
    }
  };

  // filter functions
  const handleFilterAccept = (menuTitle: string, list: IMenuList) => {
    const key = menuTitle;
    if (!filters[key] && isEmpty(list)) {
      cfg.filters[key].defaultSelected = [];
      return;
    }
    setFilters((preFilters: any) => {
      const clonedFilters = cloneDeep(preFilters);
      clonedFilters[key] = {
        key,
        list,
      };
      cfg.filters[key].defaultSelected = Object.values(list).map((item) => item.ID);
      return clonedFilters;
    });
  };

  return (
    <Panels max vertical relative minWidthMain>
      <Panels
        max
        vertical
        relative
        smallVerticalPadding
        largeHorizontalPadding
        style={{ rowGap: spacing.medium }}
      >
        <Panel style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
          <Rows>
            <Row noPadding>
              <TopBar title='Audience Manager' enableUINotices />
            </Row>
            <Row noHorizontalPadding smallVerticalPadding>
              <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Col>
                  <Text extraLarge bold disableSelect inlineBlock>
                    Liveramp Manage Distribution
                  </Text>
                </Col>
                <Col>
                  <Cols style={{ alignItems: 'center' }} gap={spacing.small}>
                    <Col>
                      <MarketplaceSegmentButton />
                    </Col>
                    <Col>
                      <ButtonV2
                        clearBackground
                        onClick={() => {
                          navigate(
                            RouteAudienceManagerSettingsList,
                            extractRouteParams({ session, user }),
                          );
                          return;
                        }}
                        style={{ padding: '5px 16px 5px 12px' }}
                      >
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col displayFlex style={{ alignItems: 'center' }}>
                            <IconV2 name='close' small color={publicaColors.blue} />
                          </Col>
                          <Col>
                            <Text sizeV2 small bold color={publicaColors.blue}>
                              Close
                            </Text>
                          </Col>
                        </Cols>
                      </ButtonV2>
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Panel>
        <Panel>
          <Text small baseWeight>
            View 'Distributed' and 'Requested' (available to distribute) segments. You can
            distribute or delete segments from your list, then create your Publica segment in the
            Segment page. Newly distributed segments will be available for use in Audience segments
            after 24 hours of processing.
          </Text>
        </Panel>
        <Panel highlighted>
          <Rows>
            <Row noPadding>
              <FilterRow
                cypressParentName={cypressClassName}
                filtersConfig={cfg.filters}
                handleFilterAccept={handleFilterAccept}
                handleClearFilters={() => {
                  setFilters({});
                }}
                enableClear={true}
              />
            </Row>
          </Rows>
        </Panel>
        <Panel highlighted largeHorizontalPadding verticalPadding>
          <Rows>
            <Row style={{ padding: '8px 0px' }}>
              <Cols style={{ justifyContent: 'space-between' }}>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.medium}
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                    }}
                  >
                    <Col>
                      <Cols
                        wrap
                        gap={spacing.medium}
                        style={{
                          alignItems: 'center',
                          rowGap: spacing.small,
                        }}
                      >
                        <Col>
                          <TextInput
                            enableClear
                            handleOnChange={handleOnChangeQuery}
                            inputStyle={
                              isSamsung ? { backgroundColor: publicaColors.veryLightGray } : {}
                            }
                            msDelay={500}
                            placeholder='Search by name'
                            prefix='search'
                          />
                        </Col>
                        <Col>
                          <SearchResultsText
                            numRowsDisplayed={responseData.length}
                            numTotalRows={totalRowCount}
                          />
                        </Col>
                      </Cols>
                    </Col>
                  </Cols>
                </Col>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.extraSmall}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      rowGap: spacing.extraSmall,
                    }}
                  >
                    <Col>
                      <ColumnSelector
                        // simple multi select props
                        preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                        resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                        columnsList={activeColumnsList}
                        columnsHandleOnChangeOption={handleChangeActiveColumns}
                        columnsHandleOnAccept={handleAcceptActiveColumns}
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
            <Row style={{ padding: '4px 4px 8px' }}>
              {selected.length >= 1 && (
                <Cols
                  wrap
                  gap={spacing.small}
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    rowGap: spacing.extraSmall,
                  }}
                >
                  <Col>
                    {/* BULK DELETE BUTTON */}
                    {bulkRemoveDisabled ? (
                      <ButtonV2
                        clearBackground
                        allCaps
                        onClick={handleOnClickBulkDelete}
                        style={{ padding: '5px 16px 5px 12px' }}
                        disabled={bulkRemoveDisabled}
                      >
                        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                          <Col paddingTop='6px'>
                            <DeleteIcon
                              style={{
                                height: '16px',
                                color: publicaColors.mediumGray,
                              }}
                            />
                          </Col>
                          <Col>
                            <Text small bold color={publicaColors.mediumGray}>
                              Remove from Distribution
                            </Text>
                          </Col>
                        </Cols>
                      </ButtonV2>
                    ) : (
                      <ButtonV2
                        clearBackground
                        allCaps
                        onClick={handleOnClickBulkDelete}
                        style={{ padding: '5px 16px 5px 12px' }}
                        disabled={bulkRemoveDisabled}
                      >
                        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                          <Col paddingTop='6px'>
                            <DeleteIcon
                              style={{
                                height: '16px',
                                color: BULK_ACTION_COLOR,
                              }}
                            />
                          </Col>
                          <Col>
                            <Text small bold color={BULK_ACTION_COLOR}>
                              Remove from Distribution
                            </Text>
                          </Col>
                        </Cols>
                      </ButtonV2>
                    )}
                  </Col>
                  <Col>
                    {/* Add to distribution BUTTON */}
                    <ButtonV2
                      clearBackground
                      allCaps
                      onClick={handleAddToDistribution}
                      style={{ padding: '5px 16px 5px 12px' }}
                    >
                      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                        <Col paddingTop='6px'>
                          <AddIcon
                            style={{
                              height: '16px',
                              color: BULK_ACTION_COLOR,
                            }}
                          />
                        </Col>
                        <Col>
                          <Text small bold color={BULK_ACTION_COLOR}>
                            Add to Distribution
                          </Text>
                        </Col>
                      </Cols>
                    </ButtonV2>
                  </Col>
                </Cols>
              )}
            </Row>
            <Row noPadding marginTop={spacing.large}>
              <ListViewTable
                activeColumnKeys={activeColumnKeys}
                columns={cfg.table.columns}
                enableSelectCheckbox
                handleChangePage={handleChangePage}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                handleOnChangeSelect={handleOnChangeSelect}
                handleDeleteItem={handleDeleteItem}
                isReadAuthorized={true}
                isSuperUser={isSuperUser}
                isWriteAuthorized={true}
                loadState={loadState}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={rows}
                rowsPerPage={rowsPerPage}
                selected={selected}
                totalRowCount={totalRowCount}
                className={cypressClassName}
                isActionPending={isTableActionPending}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleSelectAllClick={handleSelectAllClick}
                customFunctions={{ handleOpenDeleteModal }}
              />
            </Row>
          </Rows>
        </Panel>
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={handleSnackbarClose}
        >
          {snackbarMessage}
        </SnackbarView>
        <ModelWithTitleConfirmation
          active={deleteBulkModalActive}
          Name={`${selected.length} Segments`}
          deleteModel={() => {
            return handleDeleteBulkBidders();
          }}
          disableSnackbar
          onDelete={handleCloseBulkDeleteModal}
          onError={handleCloseBulkDeleteModal}
          onClose={handleCloseBulkDeleteModal}
          customMessage={
            'The selected segments will be removed from your distribution list and will no longer be accessible. Please note, requested segments cannot be removed.'
          }
          title={'Remove from Distribution'}
          confirmationMessage='Confirm removal of these segments?'
        />
        <ModelWithTitleConfirmation
          active={deleteModalActive}
          Name={`Segments`}
          deleteModel={() => {
            return handleDeleteItem(currentRowId);
          }}
          disableSnackbar
          onDelete={handleCloseDeleteModal}
          onError={handleCloseDeleteModal}
          onClose={handleCloseDeleteModal}
          customMessage={
            'The selected segment will be removed from your distribution list and will no longer be accessible. You can redistribute the segment at any time if needed.'
          }
          title={'Remove from Distribution'}
          confirmMessageWithItemName={currentItemName}
        />
      </Panels>
    </Panels>
  );
};
